import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import { Upload, Shirt, Sparkles, Maximize2, X, ChevronDown, Check, Image } from 'lucide-react';
import { demoService } from '../../services/demoService';
import { useTranslation } from '../../hooks/useTranslation';

const ImprovedDemo = () => {
  const { t } = useTranslation();
  
  // Función para obtener la URL de la aplicación principal
  const getAppUrl = () => {
    // En desarrollo, usar localhost:3001
    // En producción, usar app.luxify.ai
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    return 'https://app.luxify.ai';
  };
  const [personImage, setPersonImage] = useState(null);
  const [clothingImage, setClothingImage] = useState(null);
  const [selectedClothing, setSelectedClothing] = useState(null);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [expandedImageSrc, setExpandedImageSrc] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Cerrar dropdown al hacer click fuera
  const handleClickOutside = useCallback((event) => {
    if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
      setIsDropdownOpen(false);
    }
  }, [isDropdownOpen]);

  // Agregar event listener para cerrar dropdown
  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Función para validar tipo de imagen
  const validateImageType = (file) => {
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const fileType = file.type.toLowerCase();
    
    if (!supportedTypes.includes(fileType)) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      return {
        isValid: false,
        error: t('demoTryOn.imageFormatError', { extension: fileExtension.toUpperCase() })
      };
    }
    
    return { isValid: true };
  };

  const onPersonDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Validar tipo de archivo
      const validation = validateImageType(file);
      if (!validation.isValid) {
        setImageError(validation.error);
        return;
      }

      // Limpiar error anterior si existe
      setImageError(null);
      
      const reader = new FileReader();
      reader.onload = () => {
        setPersonImage(reader.result);
        // Limpiar error cuando se sube una imagen válida
        setImageError(null);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps: getPersonRootProps, getInputProps: getPersonInputProps, isDragActive: isPersonDragActive } = useDropzone({
    onDrop: onPersonDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    multiple: false
  });

  const handleGenerate = async () => {
    if (!personImage || !selectedClothing) return;
    
    // Validar que no hay errores de imagen
    if (imageError) {
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Subir ambas imágenes a Supabase Storage
      const uploadResult = await demoService.uploadDemoImages(
        personImage,
        selectedClothing.image,
        selectedClothing.id
      );

      if (!uploadResult.success) {
        console.error('Error subiendo imágenes:', uploadResult.error);
        // Continuar con el flujo aunque falle la subida
      }

      // Simular tiempo de generación (2-3 segundos)
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Guardar datos para el post-registro
      const demoData = {
        personImage: uploadResult.success ? uploadResult.data.userImage.publicUrl : personImage,
        clothingImage: uploadResult.success ? uploadResult.data.clothingImage.publicUrl : selectedClothing.image,
        clothingName: selectedClothing.name,
        clothingId: selectedClothing.id,
        timestamp: new Date().toISOString(),
        // Guardar también las rutas de archivos para Gemini
        userImagePath: uploadResult.success ? uploadResult.data.userImage.filePath : null,
        clothingImagePath: uploadResult.success ? uploadResult.data.clothingImage.filePath : null
      };
      
      localStorage.setItem('demoData', JSON.stringify(demoData));
      console.log('DEMO: Datos guardados en localStorage', { demoData });
      
      setIsGenerating(false);
      setHasGenerated(true);
    } catch (error) {
      console.error('Error en handleGenerate:', error);
      setIsGenerating(false);
      // Continuar con el flujo aunque haya error
      const demoData = {
        personImage,
        clothingImage: getAppImageUrl(selectedClothing.image), // Convertir a URL completa
        clothingName: selectedClothing.name,
        clothingId: selectedClothing.id,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('demoData', JSON.stringify(demoData));
      console.log('DEMO: Datos guardados en localStorage (fallback)', { demoData });
      setHasGenerated(true);
    }
  };

  // Prendas disponibles
  const availableClothing = [
    { 
      id: 'hat_2', 
      name: t('demoTryOn.clothing.hat2'), 
      image: '/images/hat_2.jpg',
      resultImage: '/images/demo-results/hat_2_result.jpg' // Imagen de resultado específica
    },
    { 
      id: 'hat', 
      name: t('demoTryOn.clothing.hat'), 
      image: '/images/hat.jpg',
      resultImage: '/images/demo-results/hat_result.jpg'
    },
    { 
      id: 'messi_shirt', 
      name: t('demoTryOn.clothing.shirt'), 
      image: '/images/messi_shirt.jpg',
      resultImage: '/images/demo-results/messi_shirt_result.jpg'
    },
    { 
      id: 'sun_glasses', 
      name: t('demoTryOn.clothing.sunglasses'), 
      image: '/images/sun_glasses.jpg',
      resultImage: '/images/demo-results/sun_glasses_result.jpg'
    },
    { 
      id: 'pantalones', 
      name: t('demoTryOn.clothing.pantalones'), 
      image: '/images/pantalones.webp',
      resultImage: '/images/demo-results/hat_result.jpg'
    },
    { 
      id: 'camisa', 
      name: t('demoTryOn.clothing.camisa'), 
      image: '/images/camisa.png',
      resultImage: '/images/demo-results/hat_result.jpg'
    },
    { 
      id: 'vestido_mujer', 
      name: t('demoTryOn.clothing.vestidomujer'), 
      image: '/images/vestidomujer.png',
      resultImage: '/images/demo-results/hat_result.jpg'
    }
  ];

  // Función para convertir rutas relativas a URLs completas para la app
  const getAppImageUrl = (relativePath) => {
    if (relativePath.startsWith('http')) {
      return relativePath; // Ya es una URL completa
    }
    
    // Convertir ruta relativa a URL completa de la app
    const appUrl = getAppUrl();
    return `${appUrl}${relativePath}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8">
        <h2 className="text-4xl sm:text-5xl font-bold text-center text-gray-900 mb-6">
          {t('demoTryOn.title')}
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto text-center">
          {t('demoTryOn.subtitle')}
        </p>
        
        {/* Progress Steps Indicator */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                1
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">{t('demoTryOn.steps.uploadPhoto')}</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                selectedClothing ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">{t('demoTryOn.steps.chooseClothing')}</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                hasGenerated ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700">{t('demoTryOn.steps.generateResult')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Person Photo Upload */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Image className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('demoTryOn.step1.title')}</h3>
              <p className="text-sm text-gray-600">{t('demoTryOn.step1.subtitle')}</p>
            </div>
            
            {!personImage ? (
              <div
                {...getPersonRootProps()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-300 ${
                  isPersonDragActive 
                    ? 'border-blue-500 bg-blue-50 scale-105' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }`}
              >
                <input {...getPersonInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2 font-medium">
                  {isPersonDragActive ? t('demoTryOn.step1.dropPhotoHere') : t('demoTryOn.step1.dragAndDropPhoto')}
                </p>
                <p className="text-sm text-gray-500">{t('demoTryOn.step1.orClickToSelect')}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {t('demoTryOn.step1.supportedFormats')}
                </p>
              </div>
            ) : (
              <div className="relative group">
                <img 
                  src={personImage} 
                  alt="Foto subida" 
                  className="w-full h-48 object-contain rounded-xl shadow-lg cursor-pointer"
                  onClick={() => { setExpandedImageSrc(personImage); setIsImageExpanded(true); }}
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => { setExpandedImageSrc(personImage); setIsImageExpanded(true); }}
                    className="w-9 h-9 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('demoTryOn.step1.expandImage')}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setPersonImage(null);
                      setImageError(null);
                    }}
                    className="w-9 h-9 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('demoTryOn.step1.removeImage')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Mostrar error de imagen si existe */}
            {imageError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <X className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 font-medium">
                      {t('demoTryOn.step1.formatError')}
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      {imageError}
                    </p>
                    <button
                      onClick={() => setImageError(null)}
                      className="text-xs text-red-600 hover:text-red-800 mt-2 underline"
                    >
                      {t('demoTryOn.step1.close')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Clothing Selection */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shirt className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('demoTryOn.step2.title')}</h3>
              <p className="text-sm text-gray-600">{t('demoTryOn.step2.subtitle')}</p>
            </div>
            
            {/* Dropdown para seleccionar prenda */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full bg-white border-2 border-gray-300 rounded-xl p-4 flex items-center justify-between hover:border-purple-300 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {selectedClothing ? (
                    <>
                      <img 
                        src={selectedClothing.image} 
                        alt={selectedClothing.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                      <span className="text-gray-700 font-medium">{selectedClothing.name}</span>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Shirt className="h-6 w-6 text-gray-400" />
                      </div>
                      <span className="text-gray-500">{t('demoTryOn.step2.selectClothing')}</span>
                    </>
                  )}
                </div>
                <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Options */}
              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
                  {availableClothing.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setClothingImage(item.image);
                        setSelectedClothing(item);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full p-4 flex items-center space-x-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                      <span className="text-gray-700 font-medium flex-1 text-left">{item.name}</span>
                      {selectedClothing && selectedClothing.id === item.id && (
                        <Check className="h-5 w-5 text-purple-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Result Preview */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('demoTryOn.step3.title')}</h3>
              <p className="text-sm text-gray-600">{t('demoTryOn.step3.subtitle')}</p>
            </div>
            
            {personImage && clothingImage && selectedClothing ? (
              <div className="relative">
                {!hasGenerated ? (
                  /* Generate Button */
                  <div className="text-center">
                    <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="h-8 w-8 text-purple-600" />
                      </div>
                      <p className="text-gray-700 font-medium mb-2">
                        {t('demoTryOn.step3.everythingReady')}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {t('demoTryOn.step3.generateVirtualResult')}
                      </p>
                    </div>
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating || imageError}
                      className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                        imageError 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                      }`}
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline"></div>
                          {t('demoTryOn.step3.generatingWithAI')}
                        </>
                      ) : imageError ? (
                        <>
                          <X className="h-5 w-5 mr-2 inline" />
                          {t('demoTryOn.step3.fixErrorToContinue')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-5 w-5 mr-2 inline" />
                          {t('demoTryOn.step3.generateImage')}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  /* Blurred Result */
                  <div>
                    <div className="relative overflow-hidden rounded-xl shadow-lg">
                      <img 
                        src={personImage} 
                        alt="Resultado blurrado" 
                        className="w-full h-48 object-cover filter blur-md"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-sm font-semibold mb-2">{t('demoTryOn.step3.resultGenerated')}</p>
                          <p className="text-xs opacity-90">{t('demoTryOn.step3.unlockByRegistering')}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Unlock Button */}
                    <button
                      onClick={() => {
                        // Guardar datos de demo en sessionStorage (compartido entre dominios)
                        const demoData = {
                          personImage: personImage,
                          clothingImage: getAppImageUrl(selectedClothing.image), // Convertir a URL completa
                          clothingName: selectedClothing.name,
                          clothingId: selectedClothing.id,
                          timestamp: new Date().toISOString()
                        };
                        
                        // Usar sessionStorage con una clave específica
                        sessionStorage.setItem('luxify_demo_data', JSON.stringify(demoData));
                        console.log('DEMO: Datos guardados en sessionStorage', { demoData });
                        
                        // Abrir la app en una nueva ventana/pestaña
                        const appWindow = window.open(`${getAppUrl()}/register`, '_blank');
                        
                        // Enviar mensaje a la nueva ventana cuando esté lista
                        if (appWindow) {
                          const checkWindow = setInterval(() => {
                            try {
                              appWindow.postMessage({
                                type: 'LUXIFY_DEMO_DATA',
                                data: demoData
                              }, getAppUrl());
                              clearInterval(checkWindow);
                            } catch (e) {
                              // Ventana aún no está lista
                            }
                          }, 100);
                        }
                      }}
                      className="w-full mt-4 bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
                    >
                      {t('demoTryOn.step3.unlockResult')}
                    </button>
                    
                    {/* Reset Button */}
                    <button
                      onClick={() => {
                        setHasGenerated(false);
                        setPersonImage(null);
                        setClothingImage(null);
                        setSelectedClothing(null);
                        setImageError(null);
                      }}
                      className="w-full mt-2 bg-gray-500 text-white py-2 px-4 rounded-xl font-semibold hover:bg-gray-600 transition-all duration-300"
                    >
                      {t('demoTryOn.step3.tryAnotherCombination')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium mb-2">
                  {t('demoTryOn.step3.waitingForSelection')}
                </p>
                <p className="text-gray-500 text-sm">
                  {t('demoTryOn.step3.uploadPhotoAndChooseClothing')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Consejo Sutil */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500 text-sm">
            <span className="text-yellow-500">💡</span>
            <span>{t('demoTryOn.tip')}</span>
          </div>
        </div>

        {/* Disclaimer del Navegador */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center space-x-2 text-amber-600 text-sm bg-amber-50 px-4 py-2 rounded-lg border border-amber-200">
            <span className="text-amber-500">⚠️</span>
            <span className="font-medium">{t('demoTryOn.browserDisclaimer')}</span>
          </div>
        </div>
      </div>

      {/* Image Expand Modal */}
      {isImageExpanded && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <img 
              src={expandedImageSrc} 
              alt={t('demoTryOn.expandedImage')} 
              className="max-w-full max-h-full object-contain rounded-xl"
            />
            <button
              onClick={() => { setIsImageExpanded(false); setExpandedImageSrc(null); }}
              className="absolute top-4 right-4 w-11 h-11 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
              title={t('demoTryOn.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ImprovedDemo;
